import { defaultOptions, type PrimeVueLocaleOptions } from "primevue/config";

const MONTH_INDEXES = Array.from({ length: 12 }, (_, i) => i);
const WEEKDAY_OFFSETS = Array.from({ length: 7 }, (_, i) => i);

/** Localized month names (day-of-month is irrelevant when formatting a month). */
const localizedMonthNames = (locale: string, format: "long" | "short") => {
  const formatter = new Intl.DateTimeFormat(locale, { month: format });
  return MONTH_INDEXES.map((month) =>
    formatter.format(new Date(2021, month, 1))
  );
};

/**
 * Localized weekday names, indexed from Sunday to match PrimeVue's convention
 * (`dayNames[0]` is Sunday). 2021-01-03 is a Sunday, so the 7 days starting
 * there map onto Sunday..Saturday.
 */
const localizedDayNames = (
  locale: string,
  format: "long" | "short" | "narrow"
) => {
  const formatter = new Intl.DateTimeFormat(locale, { weekday: format });
  return WEEKDAY_OFFSETS.map((offset) =>
    formatter.format(new Date(2021, 0, 3 + offset))
  );
};

/**
 * First day of the week per supported locale, in PrimeVue's 0=Sunday..6=Saturday
 * convention. English is Sunday-first; the app's other locales (et, lv, lt, sv)
 * are all Monday-first, so they take the `?? 1` default. Adding a Sunday-first
 * non-English locale (e.g. ja, pt-BR) is a visible edit here.
 */
const FIRST_DAY_OF_WEEK: Record<string, number> = { en: 0 };

/**
 * Build the PrimeVue locale object for the given language.
 *
 * Returns the COMPLETE locale: PrimeVue's default locale, with the calendar
 * date data localized for `locale` via `Intl` (month/day names, first day of
 * week) and the filter-mode labels overlaid with `t()` translations.
 *
 * The completeness matters. The language-switch watcher in `main.ts` assigns
 * the result straight onto `primevue.config.locale`, replacing it wholesale
 * rather than merging. PrimeVue's DatePicker reads `monthNames`, `dayNamesMin`,
 * `firstDayOfWeek`, etc. from `config.locale` to render its calendar, so a
 * partial object would strip the date locale data and the calendar would fail
 * to render after the user's language changes — breaking every date-range
 * column filter (e.g. the Company Orders "Order Date" column).
 */
export const buildPrimeVueLocale = (
  t: (key: string) => string,
  locale: string
): PrimeVueLocaleOptions => ({
  ...(defaultOptions.locale as PrimeVueLocaleOptions),
  // Calendar labels localized for the active language.
  monthNames: localizedMonthNames(locale, "long"),
  monthNamesShort: localizedMonthNames(locale, "short"),
  dayNames: localizedDayNames(locale, "long"),
  dayNamesShort: localizedDayNames(locale, "short"),
  dayNamesMin: localizedDayNames(locale, "narrow"),
  firstDayOfWeek: FIRST_DAY_OF_WEEK[locale] ?? 1,
  today: t("primevue.today"),
  // Filter-mode labels.
  startsWith: t("primevue.startsWith"),
  contains: t("primevue.contains"),
  notContains: t("primevue.notContains"),
  endsWith: t("primevue.endsWith"),
  equals: t("primevue.equals"),
  notEquals: t("primevue.notEquals"),
  noFilter: t("primevue.noFilter"),
  lt: t("primevue.lt"),
  lte: t("primevue.lte"),
  gt: t("primevue.gt"),
  gte: t("primevue.gte"),
  dateIs: t("primevue.dateIs"),
  dateIsNot: t("primevue.dateIsNot"),
  dateBefore: t("primevue.dateBefore"),
  dateAfter: t("primevue.dateAfter"),
  clear: t("primevue.clear"),
  apply: t("primevue.apply"),
  matchAll: t("primevue.matchAll"),
  matchAny: t("primevue.matchAny"),
  addRule: t("primevue.addRule"),
  removeRule: t("primevue.removeRule"),
});
